import { Emitter } from '@recurly/recurly-js/lib/emitter';
import { PayPalInstance, PayPalEvent } from '@recurly/recurly-js/lib/paypal';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { PayPalError, PayPalToken } from './PayPalCheckout';

interface EventHandlers {
    error: (data: PayPalError) => void;
    token: (data: PayPalToken) => void;
    cancel: () => void;
}
type MockOutcome = 'success' | 'error' | 'cancel';

type Listener = (...args: unknown[]) => void;

export class MockPayPalInstance implements PayPalInstance {
    private eventHandlers: Map<PayPalEvent, Listener> = new Map();
    private mockDelay = 1000; // 1 second delay to simulate network
    private outcome: MockOutcome;
    private logger = LoggerFactory.create('MockPayPalInstance');

    constructor(outcome: MockOutcome = 'success') {
        this.outcome = outcome;
    }

    public destroy(): void {}

    on(event: PayPalEvent, handler: Listener): Emitter<PayPalEvent> {
        this.eventHandlers.set(event, handler);
        return this;
    }

    start(): void {
        this.logger.info('Mock PayPal: Starting payment flow...');

        // Simulate PayPal opening and user interaction
        setTimeout(() => {
            this.simulatePayPalFlow();
        }, this.mockDelay);
    }

    private simulatePayPalFlow(): void {
        this.logger.info(`Mock PayPal: Simulating ${this.outcome} outcome`);

        switch (this.outcome) {
            case 'success':
                this.triggerSuccess();
                break;
            case 'error':
                this.triggerError();
                break;
            case 'cancel':
                this.triggerCancel();
                break;
            default:
                this.logger.warn(`Unknown outcome: ${this.outcome as string}, defaulting to success`);
                this.triggerSuccess();
        }
    }

    private triggerSuccess(): void {
        const tokenHandler = this.eventHandlers.get('token') as EventHandlers['token'];
        if (tokenHandler) {
            // Generate token ID only when actually called (client-side only)
            const timestamp = typeof window !== 'undefined' ? Date.now() : 1234567890;
            const mockToken: PayPalToken = {
                id: `mock_recurly_token_${timestamp}`,
                email: 'test.user@example.com',
                payer_id: `mock_payer_${timestamp}`,
            };

            this.logger.info({ mockToken }, 'Mock PayPal: Success - Token generated');
            tokenHandler(mockToken);
        }
    }

    private triggerError(): void {
        const errorHandler = this.eventHandlers.get('error') as EventHandlers['error'];
        if (errorHandler) {
            const mockError: PayPalError = {
                code: 'PAYPAL_MOCK_ERROR',
                message:
                    'There has been an error processing your payment. Retry your request or try a different payment method. Contact customer support at support@storyblocks.com for more help.',
                cause: 'Mock PayPal error simulation',
            };

            this.logger.error({ mockError }, 'Mock PayPal: Error triggered');
            errorHandler(mockError);
        }
    }

    private triggerCancel(): void {
        const cancelHandler = this.eventHandlers.get('cancel') as EventHandlers['cancel'];
        if (cancelHandler) {
            this.logger.info('Mock PayPal: Payment cancelled by user');
            cancelHandler();
        }
    }

    once(event: PayPalEvent, listener: Listener): Emitter<PayPalEvent> {
        throw new Error('Method not implemented.');
    }
    off(event?: PayPalEvent, listener?: Listener): Emitter<PayPalEvent> {
        throw new Error('Method not implemented.');
    }
    emit(event: PayPalEvent, ...args: unknown[]): Emitter<PayPalEvent> {
        throw new Error('Method not implemented.');
    }
    listeners(event: PayPalEvent): Listener[] {
        throw new Error('Method not implemented.');
    }
    hasListeners(event: PayPalEvent): boolean {
        throw new Error('Method not implemented.');
    }
}

export function createMockPayPalInstance(outcome: MockOutcome = 'success'): MockPayPalInstance {
    return new MockPayPalInstance(outcome);
}
